import { useEffect, useRef } from 'react';

/**
 * Native-feeling edge-swipe gestures for the mobile sidebar drawer.
 *
 *   • Swipe RIGHT starting from the left ~20 px of the screen  → open()
 *   • Swipe LEFT while the drawer is open                       → close()
 *
 * Uses passive touch listeners on `document` so it works no matter what
 * the touch starts on. Ignored automatically on screens ≥ md (768 px)
 * because the sidebar is always visible there.
 */
const EDGE_ZONE_PX = 24;      // where a right-swipe is allowed to originate
const OPEN_THRESHOLD_PX = 60; // minimum horizontal distance to trigger open
const CLOSE_THRESHOLD_PX = 60;
const MAX_VERTICAL_PX = 40;   // rejects vertical scrolls

export default function useEdgeSwipe({ isOpen, onOpen, onClose }) {
  // Refs so we don't stale-closure over open state inside the event handler.
  const openRef = useRef(isOpen);
  const onOpenRef = useRef(onOpen);
  const onCloseRef = useRef(onClose);

  useEffect(() => { openRef.current = isOpen; }, [isOpen]);
  useEffect(() => { onOpenRef.current = onOpen; }, [onOpen]);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    let startX = 0;
    let startY = 0;
    let startedNearEdge = false;

    const onTouchStart = (e) => {
      if (window.innerWidth >= 768) return; // desktop
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      startedNearEdge = startX <= EDGE_ZONE_PX;
    };

    const onTouchEnd = (e) => {
      if (window.innerWidth >= 768) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      if (dy > MAX_VERTICAL_PX) return;

      if (!openRef.current && startedNearEdge && dx >= OPEN_THRESHOLD_PX) {
        onOpenRef.current?.();
      } else if (openRef.current && dx <= -CLOSE_THRESHOLD_PX) {
        onCloseRef.current?.();
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, []);
}
